import { cx } from './cx';
import { Text, typography } from './Text';
import { useT } from '../../modules/i18n';

/** Tint a line from its content — errors red, warnings amber, else body. */
const lineTone = (line: string): string => {
  const l = line.toLowerCase();
  if (/\b(error|failed|failure|exception|fatal)\b/.test(l))
    return 'text-error';
  if (/\b(warn|warning)\b/.test(l)) return 'text-warning';
  return 'text-fg-secondary';
};

export interface LogViewerProps {
  /** Log lines, in order. */
  lines: string[];
  /** Shown when there are no lines. */
  emptyLabel?: string;
  /** Height cap — logs scroll within. Defaults to `max-h-96`. */
  maxHeightClassName?: string;
  className?: string;
}

/**
 * Terminal-style log viewer — a recessed, monospace, line-numbered block that
 * scrolls within a fixed height (long lines wrap, so it never widens the page).
 * Lines that read as errors/warnings tint from the foundation semantic tokens.
 * The shared way to show run/step logs (replaces the stock BUI log terminal).
 */
export const LogViewer = ({
  lines,
  emptyLabel,
  maxHeightClassName = 'max-h-96',
  className,
}: LogViewerProps) => {
  const t = useT();
  if (lines.length === 0) {
    return (
      <div
        className={cx(
          'rounded-lg border border-default bg-muted px-4 py-6 text-center',
          className,
        )}
      >
        <Text variant="caption" tone="tertiary">
          {emptyLabel ?? t('ui.logViewer.emptyLabel')}
        </Text>
      </div>
    );
  }

  return (
    <div
      className={cx(
        'overflow-auto rounded-lg border border-default bg-muted py-2',
        maxHeightClassName,
        className,
      )}
    >
      <div className={cx(typography.mono, 'text-xs leading-relaxed')}>
        {lines.map((line, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="flex gap-3 px-4 hover:bg-neutral">
            <span className="w-10 shrink-0 select-none text-right text-fg-muted">
              {i + 1}
            </span>
            <span
              className={cx('break-words whitespace-pre-wrap', lineTone(line))}
            >
              {line || ' '}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
